export default {
  name: 'cart',
  store() {
    return {
      // State
      data: null,
      loading: false,
      error: null,
      fetched: false,
      
      // Direct computed properties
      get items() { return this.data?.items || []; },
      get totalPrice() { return this.data?.total_price || 0; },
      get itemCount() { return this.data?.item_count || 0; },
      get subtotalPrice() { return this.data?.items_subtotal_price || 0; },
      get totalDiscount() { return this.data?.total_discount || 0; },
      get currency() { return this.data?.currency || window.store_currency || 'USD'; },
      get note() { return this.data?.note || ''; },
      get isEmpty() { return this.itemCount === 0; },
      
      // Initialize
      async init() {
        if (window.enableDebugMode) { console.log('stores/cart.js: Initializing Alpine store...'); }
        await this.fetch();
        this.setupEventListeners();
      },
      
      // Fetch cart data
      async fetch() {
        if (window.enableDebugMode) { console.log('stores/cart.js: Fetching cart data, current loading state:', this.loading); }
        try {
          this.loading = true;
          this.error = null;
          
          const response = await fetch('/cart?view=data');
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          
          this.data = await response.json();
          
          // Dispatch event for other components
          window.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: this.data }
          }));
          
        } catch (error) {
          console.error('Cart fetch error:', error);
          this.error = error.message;
        } finally {
          this.loading = false;
          this.fetched = true;
          if (window.enableDebugMode) { console.log('stores/cart.js: Finished fetching cart data:', this.data); }
        }
      },
      
      // Add item(s) to cart
      async add(variantId, quantity = 1, properties = {}) {
        try {
          this.loading = true;
          
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity, properties }]
            })
          });
          
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to add item');
          }
          
          await this.fetch(); // Refresh cart data
          
          window.dispatchEvent(new CustomEvent('cart:item_added', {
            detail: { variantId, quantity, properties }
          }));
          
          return true;
        } catch (error) {
          console.error('Add to cart error:', error);
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      
      // Update item quantity
      async updateQuantity(key, quantity) {
        try {
          this.loading = true;
          
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity })
          });
          
          if (!response.ok) throw new Error('Failed to update quantity');
          
          await this.fetch();
          
          window.dispatchEvent(new CustomEvent('cart:quantity_updated', {
            detail: { key, quantity }
          }));
          
          return true;
        } catch (error) {
          console.error('Update quantity error:', error);
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      
      // Update UI elements directly for immediate feedback
      updateItemUI(key, quantity) {
        const itemElement = document.querySelector(`[data-item-key="${key}"]`);
        if (!itemElement) return;
        
        // Update quantity input
        const quantityInput = itemElement.querySelector('[data-quantity-input]');
        if (quantityInput) {
          quantityInput.value = quantity;
        }
        
        // Hide item if quantity is 0
        const itemWrapper = itemElement.closest('.line-item-wrapper');
        if (itemWrapper && quantity <= 0) {
          itemWrapper.style.display = 'none';
        }
        
        // Update prices if we have the updated item data
        const updatedItem = this.getItemByKey(key);
        if (updatedItem) {
          const priceElement = itemWrapper?.querySelector('[data-final-price]');
          if (priceElement) {
            priceElement.textContent = this.formatPrice(updatedItem.final_line_price);
          }
        }
      },
      
      // Remove item from cart
      async remove(key) {
        return this.updateQuantity(key, 0);
      },
      
      // Update cart note
      async updateNote(note) {
        try {
          this.loading = true;
          
          const response = await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note })
          });
          
          if (!response.ok) throw new Error('Failed to update note');
          
          this.data = await response.json();
          return true;
        } catch (error) {
          console.error('Update note error:', error);
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
        }
      },
      
      // Helper methods
      getItem(variantId) {
        return this.items.find(item => item.variant_id == variantId);
      },
      
      getItemByKey(key) {
        return this.items.find(item => item.key === key);
      },
      
      hasItem(variantId) {
        return !!this.getItem(variantId);
      },
      
      formatPrice(price) {
        const symbol = window.store_currency_symbol || '$';
        return `${symbol}${(price / 100).toFixed(2)}`;
      },
      
      // Event listeners
      setupEventListeners() {
        // Refresh cart on focus (other tabs/checkout completion)
        let refreshTimeout;
        window.addEventListener('focus', () => {
          clearTimeout(refreshTimeout);
          refreshTimeout = setTimeout(() => this.fetch(), 1000);
        });
        
        document.addEventListener('visibilitychange', () => {
          if (!document.hidden) {
            clearTimeout(refreshTimeout);
            refreshTimeout = setTimeout(() => this.fetch(), 1000);
          }
        });
      }
    };
  }
};